import { id } from '@monorepo/tools/src/lib/types/primitives';
import { url } from '@monorepo/tools/src/lib/types/url';
import { IAccount } from '../models/account.model';
import { resolveUseInternalPricing } from '../models/account.model';
import { AdvertiserModel } from '../models/advertiser.model';
import { CampaignModel } from '../models/campaign.model';
import { IDestination, DestinationModel } from '../models/destination.model';
import { Statuses } from '../enums/status';

const CCID_MACRO = 'cid=@@CCID@@';
const CID_MACRO = 'cid=@@CID@@';
const CID_PARAM_PATTERN = /cid=[^&]*/i;
const CID_PARAM_WITH_AMP = /cid=[^&]*&/;
const UNRESOLVED_CID_VALUES = new Set(['@@CCID@@', '@@CID@@']);

export function isUnresolvedCidValue(value?: string): boolean {
	return !value || UNRESOLVED_CID_VALUES.has(value);
}

type DestinationWithAccount = {
	account?: { id?: id; use_internal_pricing?: boolean; useInternalPricing?: boolean };
	getAccount?: () => { getId?: () => id | undefined; getUseInternalPricing?: () => boolean | undefined } | undefined;
};

export function requiresWebappsCidForDestination(destination?: DestinationWithAccount | null): boolean {
	if (!destination) {
		return false;
	}
	if ('getAccount' in destination && destination.getAccount) {
		return destination.getAccount()?.getUseInternalPricing?.() === true;
	}
	return resolveUseInternalPricing(destination.account) === true;
}

export function applyCidToDestUrl(template: string, cid: string | number): string {
	if (template.includes(CID_MACRO)) {
		return template.replace(CID_MACRO, `cid=${cid}`);
	}
	if (template.includes(CCID_MACRO)) {
		return template.replace(CCID_MACRO, `cid=${cid}`);
	}
	if (CID_PARAM_WITH_AMP.test(template)) {
		return template.replace(CID_PARAM_WITH_AMP, `cid=${cid}&`);
	}
	if (CID_PARAM_PATTERN.test(template)) {
		return template.replace(CID_PARAM_PATTERN, `cid=${cid}`);
	}
	return template;
}

export function parseCidFromDestUrl(url: string | undefined): string | undefined {
	if (!url) {
		return undefined;
	}
	const match = url.match(/cid=([^&]*)/);
	const value = match?.[1];
	if (isUnresolvedCidValue(value)) {
		return undefined;
	}
	return value;
}

function normalizeAdditionalParams(params: string | undefined): string {
	if (!params?.trim()) {
		return '';
	}
	return params
		.trim()
		.replace(/^[?&]+/, '')
		.replace(/&+$/, '');
}

export function appendAdditionalUrlParams(url: string, additionalParams?: string): string {
	const normalized = normalizeAdditionalParams(additionalParams);
	if (!normalized) {
		return url;
	}
	const separator = url.includes('?') ? '&' : '?';
	return `${url}${separator}${normalized}`;
}

export function buildCampaignDestUrl(params: {
	template: string;
	cid?: string | number;
	additionalParams?: string;
	useInternalPricing?: boolean;
}): string {
	const { template, cid, additionalParams, useInternalPricing } = params;
	let url = template;
	if (useInternalPricing && cid != null && cid !== '') {
		url = applyCidToDestUrl(url, cid);
	}
	return appendAdditionalUrlParams(url, additionalParams);
}

function getQueryParams(url: string): Map<string, string> {
	const map = new Map<string, string>();
	const queryStart = url.indexOf('?');
	if (queryStart === -1) {
		return map;
	}
	const query = url.slice(queryStart + 1);
	query.split('&').forEach(pair => {
		const [key, ...rest] = pair.split('=');
		if (key) {
			map.set(key, rest.join('='));
		}
	});
	return map;
}

function paramsMapToString(map: Map<string, string>): string {
	return Array.from(map.entries())
		.map(([key, value]) => `${key}=${value}`)
		.join('&');
}

export interface IResolvedDestinationStateFromUrl {
	webappsCid?: string;
	additionalUrlParams: string;
}

export function resolveDestinationStateFromUrl(params: {
	template: string;
	finalUrl: string;
	useInternalPricing: boolean;
}): IResolvedDestinationStateFromUrl {
	const webappsCid = params.useInternalPricing ? parseCidFromDestUrl(params.finalUrl) : undefined;
	const additionalUrlParams = parseAdditionalUrlParams(params.template, params.finalUrl, webappsCid);

	return { webappsCid, additionalUrlParams };
}

export function resolveDestinationInternalPricing(
	destination: DestinationWithAccount | undefined | null,
	accounts?: { getId(): id | undefined; getUseInternalPricing(): boolean | undefined }[]
): boolean {
	if (requiresWebappsCidForDestination(destination)) {
		return true;
	}

	const accountId =
		destination && 'getAccount' in destination && destination.getAccount
			? destination.getAccount()?.getId?.()
			: destination?.account?.id;

	if (!accountId || !accounts?.length) {
		return false;
	}

	const account = accounts.find(item => String(item.getId()) === String(accountId));
	return account?.getUseInternalPricing() === true;
}

export function parseAdditionalUrlParams(template: string, finalUrl: string, resolvedCid?: string | number): string {
	const templateParams = getQueryParams(template);
	const finalParams = getQueryParams(finalUrl);
	const additional = new Map<string, string>();

	const shouldSkipCidParam = (value: string, templateCid?: string): boolean => {
		if (isUnresolvedCidValue(templateCid)) {
			return true;
		}
		if (resolvedCid != null && String(value) === String(resolvedCid)) {
			return true;
		}
		return templateCid === value;
	};

	finalParams.forEach((value, key) => {
		if (key === 'cid' && shouldSkipCidParam(value, templateParams.get('cid'))) {
			return;
		}
		if (!templateParams.has(key) || templateParams.get(key) !== value) {
			additional.set(key, value);
		}
	});

	return paramsMapToString(additional);
}

function normalizeDestUrlForMatch(url: string): string {
	return url
		.replace(CID_MACRO, 'cid=__CID__')
		.replace(CCID_MACRO, 'cid=__CID__')
		.replace(CID_PARAM_WITH_AMP, 'cid=__CID__&')
		.replace(CID_PARAM_PATTERN, 'cid=__CID__');
}

export function resolveDestinationFromUrl(destinations: IDestination[], destUrl: string | undefined): IDestination | undefined {
	if (!destUrl || destinations.length === 0) {
		return undefined;
	}

	const exact = destinations.find(d => d.dest_url === destUrl);
	if (exact) {
		return exact;
	}

	const normalizedDestUrl = normalizeDestUrlForMatch(destUrl);
	return destinations.find(d => normalizeDestUrlForMatch(d.dest_url) === normalizedDestUrl);
}

export function getCampaignDestinationActivationError(campaign: CampaignModel, options?: { campaignIsActive?: boolean }): string | null {
	const campaignIsActive = options?.campaignIsActive ?? campaign.getStatus() === Statuses.Active;
	return getDestinationActivationErrors({
		destinations: campaign.getCampaignDestinations().map(link => ({
			selectedDestinationId: link.getSelectedDestinationId(),
			useInternalPricing: link.getDestinationUseInternalPricing(),
			selectedWebappsCid: link.getSelectedWebappsCid(),
			destUrl: link.getDestinationUrl(),
			status: link.status,
		})),
		campaignIsActive,
	});
}

export function getDestinationActivationErrors(params: {
	destinations: Array<{
		selectedDestinationId?: string | number;
		useInternalPricing?: boolean;
		selectedWebappsCid?: string | number;
		destUrl?: string;
		status?: string;
	}>;
	campaignIsActive?: boolean;
}): string | null {
	if (!params.destinations.length) {
		return 'Please select at least one destination';
	}

	for (const destination of params.destinations) {
		if (!destination.selectedDestinationId) {
			return 'Please complete destination configuration';
		}

		if (destination.useInternalPricing && !destination.selectedWebappsCid) {
			return 'Please select a Web Apps CID for all destinations that require it';
		}

		if (!destination.destUrl) {
			return 'Please complete the destination configuration';
		}
	}

	if (params.campaignIsActive) {
		const hasActiveDestination = params.destinations.some(destination => destination.status !== Statuses.Paused);
		if (!hasActiveDestination) {
			return 'An active campaign must have at least one active destination';
		}
	}

	return null;
}

export function filterDestinationsByAdvertiser(destinations: DestinationModel[], advertiserId?: id): DestinationModel[] {
	if (!advertiserId) {
		return [];
	}
	return destinations.filter(destination => String(destination.getAdvertiser()?.getId()) === String(advertiserId));
}

export interface ICampaignDestinationDtoPayload {
	id?: id;
	name?: string;
	dest_url?: url;
	yid?: string;
	account?: IAccount;
	advertiser?: { id?: id; name?: string; user_cap_enabled?: boolean };
}

export function buildCampaignDestinationDtoFromDestination(
	destination: DestinationModel,
	advertiserFallback?: AdvertiserModel
): ICampaignDestinationDtoPayload {
	const account = destination.getAccount();
	const advertiser = destination.getAdvertiser() ?? advertiserFallback;

	return {
		id: destination.getId(),
		name: destination.getName(),
		dest_url: destination.getDestUrl(),
		yid: destination.getYid() || undefined,
		account: account
			? {
					id: account.getId(),
					name: account.getName(),
					use_internal_pricing: account.getUseInternalPricing(),
					use_calc_payout: account.getUseCalcPayout(),
				}
			: undefined,
		advertiser: advertiser
			? {
					id: advertiser.getId(),
					name: advertiser.getName(),
					user_cap_enabled: advertiser.getUserCapEnabled(),
				}
			: undefined,
	};
}
